import { useEffect, useMemo, useState, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetFooter,
} from "@/components/ui/sheet";
import { Plus, Trash2, Pencil, Download, Upload, FileSpreadsheet, Search, Image as ImageIcon, X as XIcon, FileText } from "lucide-react";
import { toast } from "sonner";

// Phase 35.2 — thumbnail cell used in the Items table. Fetches the product
// image via api (so the Bearer token is attached) and renders it as an object
// URL. FG items get an upload/replace/remove control, everything else stays blank.
function ItemImageCell({ item, onChanged }) {
  const [preview, setPreview] = useState("");   // object URL
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const hasImage = !!item.image_storage_path;

  useEffect(() => {
    let revoke = "";
    (async () => {
      if (item.type !== "FG" || !hasImage) { setPreview(""); return; }
      try {
        const res = await api.get(`/items/${item.id}/image`, { responseType: "blob" });
        const url = URL.createObjectURL(res.data);
        revoke = url;
        setPreview(url);
      } catch { setPreview(""); }
    })();
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [item.id, item.image_storage_path, item.type, hasImage]);

  if (item.type !== "FG") return <span className="text-slate-300 text-xs">—</span>;

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/items/${item.id}/image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Image uploaded");
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Remove product image?")) return;
    setBusy(true);
    try {
      await api.delete(`/items/${item.id}/image`);
      toast.success("Image removed");
      onChanged?.();
    } finally { setBusy(false); }
  };

  return (
    <div className="flex items-center gap-2" data-testid={`item-image-cell-${item.code}`}>
      {preview ? (
        <img src={preview} alt="" className="w-10 h-10 object-contain rounded border border-slate-200" data-testid={`item-image-thumb-${item.code}`} />
      ) : (
        <div className="w-10 h-10 rounded border border-dashed border-slate-200 flex items-center justify-center text-slate-300">
          <ImageIcon className="w-4 h-4" />
        </div>
      )}
      <div className="flex flex-col gap-0.5">
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          className="text-[10px] uppercase tracking-wider text-blue-600 hover:text-blue-700 disabled:opacity-40"
          data-testid={`item-image-upload-${item.code}`}
        >
          {hasImage ? "Replace" : "Upload"}
        </button>
        {hasImage && (
          <button
            type="button"
            onClick={remove}
            disabled={busy}
            className="text-[10px] uppercase tracking-wider text-slate-500 hover:text-red-600 disabled:opacity-40"
            data-testid={`item-image-remove-${item.code}`}
          >
            <XIcon className="w-3 h-3 inline -mt-0.5" /> Remove
          </button>
        )}
      </div>
      <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={upload} data-testid={`item-image-input-${item.code}`} />
    </div>
  );
}

// Opens an item's Drawing / Data Sheet PDF in a new tab. Fetched via api so
// the Bearer token is attached.
async function openDatasheet(item) {
  const win = window.open("", "_blank");
  try {
    const res = await api.get(`/items/${item.id}/datasheet`, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    if (win) win.location.href = url; else window.location.assign(url);
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (err) {
    if (win) win.close();
    toast.error("Could not open drawing / data sheet");
  }
}

// Drawing / Data Sheet cell in the Items table — optional PDF per item.
function ItemDatasheetCell({ item, onChanged }) {
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const hasSheet = !!item.datasheet_storage_path;

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Drawing / Data Sheet must be a PDF file");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/items/${item.id}/datasheet`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Drawing / Data Sheet uploaded");
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-2" data-testid={`item-datasheet-cell-${item.code}`}>
      {hasSheet ? (
        <button type="button" onClick={() => openDatasheet(item)} title={item.datasheet_filename || "View PDF"}
          className="w-10 h-10 rounded border border-red-200 bg-red-50 flex items-center justify-center text-red-600 hover:bg-red-100"
          data-testid={`item-datasheet-view-${item.code}`}>
          <FileText className="w-4 h-4" />
        </button>
      ) : (
        <div className="w-10 h-10 rounded border border-dashed border-slate-200 flex items-center justify-center text-slate-300">
          <FileText className="w-4 h-4" />
        </div>
      )}
      <div className="flex flex-col gap-0.5">
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          className="text-[10px] uppercase tracking-wider text-blue-600 hover:text-blue-700 disabled:opacity-40 text-left"
          data-testid={`item-datasheet-upload-${item.code}`}
        >
          {hasSheet ? "Replace" : "Upload PDF"}
        </button>
      </div>
      <input ref={fileRef} type="file" accept="application/pdf,.pdf" className="hidden" onChange={upload} data-testid={`item-datasheet-input-${item.code}`} />
    </div>
  );
}

const empty = { code: "", name: "", type: "FG", uom: "PCS", cost: 0, description: "", bom: [], moq: 0, min_stock_level: 0, reorder_qty: 0, lead_time_days: 0, supplier: "", material_category: "", grade: "", size: "", hsn_code: "", color: "", parent_fg_item_id: "", safety_stock: 0, is_regular: false, is_non_regular: false, revision: "" };

// Procurement classification used by the automated MRP run
// (backend/auto_procurement.py). Regular and Non-Regular are mutually
// exclusive — ticking one clears the other.
function procurementClass(it) {
  if (it.is_regular && !it.is_non_regular) return "regular";
  if (it.is_non_regular && !it.is_regular) return "non_regular";
  return "";
}

// Stage-specific item types (the part number a batch is booked under while
// sitting at a given production stage) — these are the ones that need a
// Linked Finished Good so MRP Run can roll their WIP up automatically.
const STAGE_ITEM_TYPES = ["MS", "IMP", "FAB", "PWD", "SFG", "ASM", "TEST", "FI"];

export default function ItemsPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  // Drawing / Data Sheet PDF picked in the form — uploaded right after save.
  const [datasheetFile, setDatasheetFile] = useState(null);
  const [existingDatasheet, setExistingDatasheet] = useState("");

  const load = async () => {
    const { data } = await api.get("/items");
    setItems(data);
  };

  useEffect(() => { load(); }, []);

  const startCreate = () => {
    setForm(empty); setEditing(null); setDatasheetFile(null); setExistingDatasheet(""); setOpen(true);
  };
  const startEdit = (it) => {
    setForm({
      code: it.code, name: it.name, type: it.type, uom: it.uom, cost: it.cost,
      description: it.description || "", bom: it.bom || [],
      moq: it.moq || 0, min_stock_level: it.min_stock_level || 0, reorder_qty: it.reorder_qty || 0,
      lead_time_days: it.lead_time_days || 0, supplier: it.supplier || "",
      material_category: it.material_category || "", grade: it.grade || "", size: it.size || "",
      hsn_code: it.hsn_code || "", color: it.color || "",
      parent_fg_item_id: it.parent_fg_item_id || "",
      safety_stock: it.safety_stock || 0,
      is_regular: !!it.is_regular, is_non_regular: !!it.is_non_regular,
      revision: it.revision || "",
    });
    setDatasheetFile(null);
    setExistingDatasheet(it.datasheet_storage_path ? (it.datasheet_filename || "datasheet.pdf") : "");
    setEditing(it.id);
    setOpen(true);
  };

  const save = async () => {
    if (busy) return;
    if (form.is_regular && form.is_non_regular) {
      toast.error("Select either Regular or Non-Regular — not both.");
      return;
    }
    if (!form.is_regular && !form.is_non_regular
      && !window.confirm("Neither Regular nor Non-Regular is selected — the automated MRP run will skip this item (unless it inherits a class from its BOM). Save anyway?")) {
      return;
    }
    setBusy(true);
    try {
      const { data: saved } = editing
        ? await api.put(`/items/${editing}`, form)
        : await api.post("/items", form);
      if (datasheetFile) {
        try {
          const fd = new FormData();
          fd.append("file", datasheetFile);
          await api.post(`/items/${saved.id}/datasheet`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        } catch (err) {
          toast.error(`Item saved, but the Drawing / Data Sheet upload failed: ${formatApiError(err.response?.data?.detail) || "Upload failed"}`);
          setOpen(false);
          await load();
          return;
        }
      }
      toast.success(editing ? "Item updated" : "Item created");
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  // Inline Regular / Non-Regular toggle from the table. The item's current
  // fields are re-sent unchanged; min-stock / safety-stock are omitted, which
  // the PUT treats as "leave as is".
  const setClass = async (it, cls) => {
    const patch = { is_regular: cls === "regular", is_non_regular: cls === "non_regular" };
    try {
      await api.put(`/items/${it.id}`, {
        code: it.code, name: it.name, type: it.type, uom: it.uom, cost: it.cost || 0,
        description: it.description || "", bom: it.bom || [], moq: it.moq || 0,
        lead_time_days: it.lead_time_days || 0, supplier: it.supplier || "",
        material_category: it.material_category || "", grade: it.grade || "", size: it.size || "",
        hsn_code: it.hsn_code || "", color: it.color || "", parent_fg_item_id: it.parent_fg_item_id || "",
        ...patch,
      });
      setItems((prev) => prev.map((x) => (x.id === it.id ? { ...x, ...patch } : x)));
      toast.success(`${it.code}: ${cls === "regular" ? "Regular" : cls === "non_regular" ? "Non-Regular" : "classification cleared"}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await api.delete(`/items/${id}`);
      toast.success("Item deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };

  const downloadCsv = async (path, filename) => {
    try {
      const { data } = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = filename; document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed"); }
  };

  const onImport = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/items/import-csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported ${file.name} · created ${data.created}, updated ${data.updated}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Import failed");
    }
  };


  const addBomRow = () => setForm((f) => ({ ...f, bom: [...f.bom, { item_id: "", qty: 1, scrap_pct: 0 }] }));
  const setBomRow = (i, patch) => setForm((f) => ({
    ...f, bom: f.bom.map((r, idx) => idx === i ? { ...r, ...patch } : r),
  }));
  const removeBomRow = (i) => setForm((f) => ({ ...f, bom: f.bom.filter((_, idx) => idx !== i) }));

  const itemMap = Object.fromEntries(items.map((i) => [i.id, i]));
  const componentChoices = items.filter((i) => i.id !== editing);

  // Phase 28 — tab filter for "SFG BOMs" (and All / FG BOMs) so operators can
  // review the assembly BOMs separately from FG BOMs.
  const [typeTab, setTypeTab] = useState("all");
  // Phase 29 — real-time search by Part Code / Part Name
  const [search, setSearch] = useState("");

  const visibleItems = useMemo(() => {
    let list = items;
    if (typeTab === "fg_bom") list = list.filter((i) => i.type === "FG");
    else if (typeTab === "sfg_bom") list = list.filter((i) => i.type === "SFG");
    else if (typeTab === "raw") list = list.filter((i) => i.type === "RAW");
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((i) =>
        (i.code || "").toLowerCase().includes(q) ||
        (i.name || "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [items, typeTab, search]);

  return (
    <div className="space-y-6" data-testid="items-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Master Data</div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>
            Items & Bill of Materials
          </h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative" data-testid="items-search-wrap">
            <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by part code or name…"
              className="pl-8 w-64 h-9"
              data-testid="items-search-input"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] uppercase tracking-wider text-slate-400 hover:text-slate-700"
                data-testid="items-search-clear"
              >
                Clear
              </button>
            )}
          </div>
          <Button variant="outline" onClick={() => downloadCsv("/items/template-csv", "items_template.csv")} data-testid="csv-template-btn">
            <FileSpreadsheet className="w-4 h-4 mr-1.5" /> Template
          </Button>
          <Button variant="outline" onClick={() => downloadCsv("/items/export-csv", "items_export.csv")} data-testid="csv-export-btn">
            <Download className="w-4 h-4 mr-1.5" /> Export
          </Button>
          <label className="inline-flex items-center px-3 h-9 rounded-md border border-slate-200 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 cursor-pointer" data-testid="csv-import-label">
            <Upload className="w-4 h-4 mr-1.5" /> Import CSV
            <input type="file" accept=".csv,text/csv" className="hidden" onChange={onImport} data-testid="csv-import-input" />
          </label>
          <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={startCreate} data-testid="new-item-btn">
              <Plus className="w-4 h-4 mr-1.5" /> New Item
            </Button>
          </SheetTrigger>
          <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
            <SheetHeader>
              <SheetTitle>{editing ? "Edit Item" : "Create Item"}</SheetTitle>
            </SheetHeader>
            <div className="space-y-4 mt-6">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Code</Label>
                  <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} data-testid="item-code-input" />
                </div>
                <div>
                  <Label>Name</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="item-name-input" />
                </div>
                <div>
                  <Label>Type</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger data-testid="item-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="FG">Finished Good</SelectItem>
                      <SelectItem value="SFG">Semi-Finished</SelectItem>
                      <SelectItem value="RAW">Raw Material</SelectItem>
                      <SelectItem value="MS">Machine Shop Store</SelectItem>
                      <SelectItem value="IMP">Impregnation Store</SelectItem>
                      <SelectItem value="FAB">Fabrication Store</SelectItem>
                      <SelectItem value="PWD">Powder Coating Store</SelectItem>
                      <SelectItem value="ASM">Assembly Store</SelectItem>
                      <SelectItem value="TEST">Testing Store</SelectItem>
                      <SelectItem value="FI">Final Inspection Store</SelectItem>
                      <SelectItem value="RWK">Rework Store</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>UoM</Label>
                  <Input value={form.uom} onChange={(e) => setForm({ ...form, uom: e.target.value })} />
                </div>
                <div className="col-span-2">
                  <Label>Cost (per UoM)</Label>
                  <Input type="number" value={form.cost} onChange={(e) => setForm({ ...form, cost: parseFloat(e.target.value || 0) })} />
                </div>
                <div className="col-span-2">
                  <Label>Description</Label>
                  <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                </div>
                {/* MOQ is available for ALL item types (FG, SFG, RAW, MS, IMP, PWD, ASM, TEST, FI, RWK) */}
                <div>
                  <Label>MOQ (Min Order Qty)</Label>
                  <Input type="number" value={form.moq} onChange={(e) => setForm({ ...form, moq: parseFloat(e.target.value || 0) })} data-testid="item-moq-input" />
                </div>
                {/* Minimum Stock Level drives the MRP min-stock alert + supplier email;
                    Reorder Qty is the standard quantity requested when it fires. */}
                <div>
                  <Label title="MRP alerts when Available (Physical − Reserved) reaches this level. 0 = use the store reorder point.">Min Stock Level</Label>
                  <Input type="number" min="0" value={form.min_stock_level} onChange={(e) => setForm({ ...form, min_stock_level: parseFloat(e.target.value || 0) })} data-testid="item-min-stock-input" />
                </div>
                <div>
                  <Label>Reorder Qty</Label>
                  <Input type="number" min="0" value={form.reorder_qty} onChange={(e) => setForm({ ...form, reorder_qty: parseFloat(e.target.value || 0) })} data-testid="item-reorder-qty-input" />
                </div>
                <div>
                  <Label title="Added on top of the reorder threshold by the automated MRP run">Safety Stock</Label>
                  <Input type="number" min="0" value={form.safety_stock} onChange={(e) => setForm({ ...form, safety_stock: parseFloat(e.target.value || 0) })} data-testid="item-safety-stock-input" />
                </div>
                {form.type !== "RAW" && (
                  <div className="col-span-2">
                    <Label title="Must match a Supplier Master name or code — min-stock alerts are emailed to that supplier">Supplier</Label>
                    <Input value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} data-testid="item-supplier-input-any" />
                  </div>
                )}
                {form.type === "RAW" && (
                  <>
                    <div>
                      <Label>Lead Time (days)</Label>
                      <Input type="number" value={form.lead_time_days} onChange={(e) => setForm({ ...form, lead_time_days: parseInt(e.target.value || 0, 10) })} />
                    </div>
                    <div className="col-span-2">
                      <Label>Supplier</Label>
                      <Input value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} data-testid="item-supplier-input" />
                    </div>
                    <div>
                      <Label>Material Category</Label>
                      <select className="w-full h-9 border border-slate-200 rounded-md px-2 text-sm bg-white" value={form.material_category} onChange={(e) => setForm({ ...form, material_category: e.target.value })} data-testid="item-material-cat">
                        <option value="">— None —</option>
                        <option value="AL_PIPE">Aluminium Pipe</option>
                        <option value="AL_ROD">Aluminium Rod</option>
                        <option value="AL_SHEET">Aluminium Sheet</option>
                        <option value="AL_PROFILE">Aluminium Profile</option>
                        <option value="OTHER">Other Purchased Material</option>
                      </select>
                    </div>
                    <div>
                      <Label>Grade</Label>
                      <Input value={form.grade} onChange={(e) => setForm({ ...form, grade: e.target.value })} placeholder="AA 6063-T6" data-testid="item-grade" />
                    </div>
                    <div className="col-span-2">
                      <Label>Size / Spec</Label>
                      <Input value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })} placeholder='1.5" OD x 1.2 mm wall' data-testid="item-size" />
                    </div>
                  </>
                )}
                {form.type !== "RAW" && (
                  <>
                    <div>
                      <Label>HSN Code</Label>
                      <Input value={form.hsn_code} onChange={(e) => setForm({ ...form, hsn_code: e.target.value })} placeholder="7608" data-testid="item-hsn" />
                    </div>
                    <div>
                      <Label>Color (if applicable)</Label>
                      <Input value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} placeholder="White / Black / Anodized" data-testid="item-color" />
                    </div>
                  </>
                )}
                {STAGE_ITEM_TYPES.includes(form.type) && (
                  <div className="col-span-2">
                    <Label>Linked Finished Good</Label>
                    <Select value={form.parent_fg_item_id || ""} onValueChange={(v) => setForm({ ...form, parent_fg_item_id: v })}>
                      <SelectTrigger data-testid="item-parent-fg-select"><SelectValue placeholder="— None —" /></SelectTrigger>
                      <SelectContent>
                        {items.filter((i) => (i.type === "FG" || i.type === "SFG") && i.id !== editing).map((i) => (
                          <SelectItem key={i.id} value={i.id}>{i.code} — {i.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-[11px] text-slate-500 mt-1">
                      Set once here — every Production Job that ever uses this part number then rolls its quantity up to that Finished Good's row in MRP Run automatically, with nothing to link per batch.
                    </p>
                  </div>
                )}
              </div>

              {form.type !== "RAW" && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-sm font-semibold text-slate-700">Bill of Materials</div>
                    <Button variant="outline" size="sm" onClick={addBomRow} data-testid="add-bom-row">
                      <Plus className="w-3.5 h-3.5 mr-1" /> Add component
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {form.bom.length === 0 && <div className="text-xs text-slate-500">No components yet.</div>}
                    {form.bom.map((row, idx) => (
                      <div key={idx} className="grid grid-cols-12 gap-2 items-end">
                        <div className="col-span-7">
                          <Label className="text-[10px] uppercase tracking-wider text-slate-500">Component</Label>
                          <Select value={row.item_id} onValueChange={(v) => setBomRow(idx, { item_id: v })}>
                            <SelectTrigger data-testid={`bom-component-${idx}`}><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>
                              {componentChoices.map((it) => (
                                <SelectItem key={it.id} value={it.id}>{it.code} — {it.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-2">
                          <Label className="text-[10px] uppercase tracking-wider text-slate-500">Qty</Label>
                          <Input type="number" step="0.01" value={row.qty} onChange={(e) => setBomRow(idx, { qty: parseFloat(e.target.value || 0) })} />
                        </div>
                        <div className="col-span-2">
                          <Label className="text-[10px] uppercase tracking-wider text-slate-500">Scrap %</Label>
                          <Input type="number" step="0.01" value={row.scrap_pct} onChange={(e) => setBomRow(idx, { scrap_pct: parseFloat(e.target.value || 0) })} />
                        </div>
                        <div className="col-span-1">
                          <Button variant="ghost" size="icon" onClick={() => removeBomRow(idx)}>
                            <Trash2 className="w-4 h-4 text-slate-500" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Drawing / Data Sheet (optional PDF) + Revision */}
              <div className="border-t border-slate-100 pt-4 grid grid-cols-3 gap-3" data-testid="item-datasheet-section">
                <div className="col-span-2">
                  <Label>Drawing / Data Sheet (PDF) <span className="text-slate-400 font-normal">(optional)</span></Label>
                  <Input type="file" accept="application/pdf,.pdf" className="mt-1"
                    onChange={(e) => {
                      const f = e.target.files?.[0] || null;
                      if (f && f.type !== "application/pdf" && !f.name.toLowerCase().endsWith(".pdf")) {
                        toast.error("Drawing / Data Sheet must be a PDF file");
                        e.target.value = "";
                        setDatasheetFile(null);
                        return;
                      }
                      setDatasheetFile(f);
                    }}
                    data-testid="item-datasheet-input" />
                  <div className="text-[11px] mt-1">
                    {datasheetFile ? (
                      <span className="text-slate-600">Will upload: {datasheetFile.name}</span>
                    ) : existingDatasheet ? (
                      <span className="text-slate-600">Current: {existingDatasheet} — choose a file to replace it.</span>
                    ) : (
                      <span className="text-slate-500">No PDF attached.</span>
                    )}
                  </div>
                </div>
                <div>
                  <Label>Revision</Label>
                  <Input value={form.revision} onChange={(e) => setForm({ ...form, revision: e.target.value })}
                    placeholder="e.g. A / R01" className="mt-1" data-testid="item-revision-input" />
                </div>
              </div>

              {/* Procurement classification — last section of the Item / BOM form. */}
              <div className="border-t border-slate-100 pt-4" data-testid="item-procurement-class">
                <div className="text-sm font-semibold text-slate-700 mb-1">Procurement (Automated MRP)</div>
                <p className="text-[11px] text-slate-500 mb-2">
                  Regular — MRP raises the PR and PO and sends the PO to the supplier by email + WhatsApp automatically.
                  Non-Regular — MRP raises a PR and routes it to the Purchase Team.
                  {form.bom.length > 0 && " Set on a BOM, the class also applies to any component that has no class of its own."}
                </p>
                <div className="flex items-center gap-6">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" className="w-4 h-4 accent-blue-600" checked={!!form.is_regular}
                      onChange={(e) => setForm({ ...form, is_regular: e.target.checked, is_non_regular: e.target.checked ? false : form.is_non_regular })}
                      data-testid="item-regular-checkbox" />
                    Regular
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" className="w-4 h-4 accent-amber-600" checked={!!form.is_non_regular}
                      onChange={(e) => setForm({ ...form, is_non_regular: e.target.checked, is_regular: e.target.checked ? false : form.is_regular })}
                      data-testid="item-non-regular-checkbox" />
                    Non-Regular
                  </label>
                </div>
                {!form.is_regular && !form.is_non_regular && (
                  <div className="text-[11px] text-amber-700 mt-1" data-testid="item-class-warning">
                    Select Regular or Non-Regular so the automated MRP run can procure this item.
                  </div>
                )}
              </div>
            </div>
            <SheetFooter className="mt-6">
              <Button onClick={save} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="save-item-btn">
                {editing ? "Update" : "Create"} Item
              </Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100 flex-wrap" data-testid="items-type-tabs">
          {[
            { key: "all",     label: "All Items" },
            { key: "fg_bom",  label: "FG BOMs" },
            { key: "sfg_bom", label: "SFG BOMs" },
            { key: "raw",     label: "Raw Materials" },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setTypeTab(t.key)}
              data-testid={`items-tab-${t.key}`}
              className={`text-xs px-3 py-1.5 rounded-full border transition ${typeTab === t.key ? "bg-blue-600 text-white border-blue-600" : "bg-white border-slate-200 text-slate-700 hover:border-blue-300"}`}
            >
              {t.label} <span className="ml-1 mono text-[10px] opacity-70">
                {t.key === "all" ? items.length
                  : t.key === "fg_bom" ? items.filter((i) => i.type === "FG").length
                  : t.key === "sfg_bom" ? items.filter((i) => i.type === "SFG").length
                  : items.filter((i) => i.type === "RAW").length}
              </span>
            </button>
          ))}
        </div>
        {/* The table scrolls inside its own box, sized to the window, so the
            horizontal scrollbar is always on screen (not after ~3,000 rows)
            and the header row stays visible while scrolling down. */}
        <div className="overflow-auto max-h-[calc(100vh-300px)] min-h-[320px]" data-testid="items-table-scroll">
        <table className="w-full min-w-[1500px] text-sm" data-testid="items-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-[0.14em] text-slate-500 bg-slate-50 whitespace-nowrap [&>th]:sticky [&>th]:top-0 [&>th]:z-10 [&>th]:bg-slate-50">
              <th className="py-3 px-4">Code</th>
              <th className="py-3 px-4">Name</th>
              <th className="py-3 px-4">Image</th>
              <th className="py-3 px-4">Type</th>
              <th className="py-3 px-4">UoM</th>
              <th className="py-3 px-4">Cost</th>
              <th className="py-3 px-4 text-right">MOQ</th>
              <th className="py-3 px-4">BOM / Supplier</th>
              <th className="py-3 px-4 text-center">Regular</th>
              <th className="py-3 px-4 text-center">Non-Regular</th>
              <th className="py-3 px-4">Drawing / Data Sheet</th>
              <th className="py-3 px-4">Revision</th>
              <th className="py-3 px-4 text-right right-0 !z-20 shadow-[-6px_0_6px_-6px_rgba(15,23,42,0.18)]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {visibleItems.map((it) => (
              <tr key={it.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`item-row-${it.code}`}>
                <td className="py-3 px-4 font-medium text-slate-800 mono">{it.code}</td>
                <td className="py-3 px-4 text-slate-700">{it.name}</td>
                <td className="py-3 px-4">
                  <ItemImageCell item={it} onChanged={load} />
                </td>
                <td className="py-3 px-4">
                  <span className={`text-[11px] uppercase tracking-wider px-2 py-0.5 rounded-full
                    ${it.type === "FG" ? "bg-blue-50 text-blue-700" : it.type === "SFG" ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-600"}`}>
                    {it.type}
                  </span>
                </td>
                <td className="py-3 px-4 text-slate-600">{it.uom}</td>
                <td className="py-3 px-4 mono">{it.cost}</td>
                <td className="py-3 px-4 mono text-right" data-testid={`item-moq-cell-${it.code}`}>{it.moq ? it.moq : "—"}</td>
                <td className="py-3 px-4 text-slate-600">
                  {it.type === "RAW" ? (
                    <span className="text-xs">
                      {it.supplier ? `${it.supplier}` : "—"}{it.moq ? ` · MOQ ${it.moq}` : ""}
                    </span>
                  ) : (
                    <span className="text-xs">
                      {(it.bom || []).length === 0 ? "—" : (it.bom || []).map((b) => `${itemMap[b.item_id]?.code || "?"} ×${b.qty}`).join(", ")}
                      {STAGE_ITEM_TYPES.includes(it.type) && (
                        it.parent_fg_item_id && itemMap[it.parent_fg_item_id] ? (
                          <div className="text-emerald-700 mt-0.5" data-testid={`item-parent-fg-chip-${it.code}`}>→ {itemMap[it.parent_fg_item_id].code}</div>
                        ) : (
                          <div className="text-amber-700 mt-0.5" data-testid={`item-parent-fg-missing-${it.code}`}>No Finished Good linked</div>
                        )
                      )}
                    </span>
                  )}
                </td>
                <td className="py-3 px-4 text-center" data-testid={`item-regular-cell-${it.code}`}>
                  <input type="checkbox" className="w-4 h-4 accent-blue-600 cursor-pointer" checked={procurementClass(it) === "regular"}
                    onChange={(e) => setClass(it, e.target.checked ? "regular" : "")} aria-label={`${it.code} Regular`} />
                </td>
                <td className="py-3 px-4 text-center" data-testid={`item-non-regular-cell-${it.code}`}>
                  <input type="checkbox" className="w-4 h-4 accent-amber-600 cursor-pointer" checked={procurementClass(it) === "non_regular"}
                    onChange={(e) => setClass(it, e.target.checked ? "non_regular" : "")} aria-label={`${it.code} Non-Regular`} />
                </td>
                <td className="py-3 px-4">
                  <ItemDatasheetCell item={it} onChanged={load} />
                </td>
                <td className="py-3 px-4 mono text-slate-700" data-testid={`item-revision-cell-${it.code}`}>{it.revision || "—"}</td>
                {/* Edit / Delete stay pinned to the right edge while scrolling sideways. */}
                <td className="py-3 px-4 text-right whitespace-nowrap sticky right-0 bg-white shadow-[-6px_0_6px_-6px_rgba(15,23,42,0.18)]">
                  <Button variant="ghost" size="icon" onClick={() => startEdit(it)} data-testid={`edit-item-${it.code}`}>
                    <Pencil className="w-4 h-4 text-slate-500" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => remove(it.id)} data-testid={`delete-item-${it.code}`}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </td>
              </tr>
            ))}
            {visibleItems.length === 0 && (
              <tr>
                <td colSpan={13} className="py-10 text-center text-slate-500 text-sm" data-testid="items-empty-state">
                  {items.length === 0
                    ? "No items yet."
                    : search
                      ? <>No items match <span className="mono text-slate-700">“{search}”</span>.</>
                      : "No items in this filter."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}
